import { CognitoHostedUIIdentityProvider } from '@aws-amplify/auth/lib/types'
import { Auth } from 'aws-amplify'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { Icons } from '@/components/ui/icons'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/components/ui/use-toast'
import { cn } from '@/lib/utils'
import { useAuth } from '@/providers/AuthContextProvider'
import { RoutePaths } from '@/routes/routes'

interface UserAuthFormProps extends React.HTMLAttributes<HTMLDivElement> {}

export function UserAuthForm({ className, ...props }: UserAuthFormProps) {
  const { setUserData } = useAuth()
  const { toast } = useToast()
  const [email, setEmail] = useState<string>('')
  const [password, setPassword] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const navigate = useNavigate()

  async function onSubmit(event: React.SyntheticEvent) {
    event.preventDefault()
    setIsLoading(true)
    try {
      const authData = await Auth.signIn(email, password)

      setUserData(authData)
      navigate(RoutePaths.HOME)
    } catch (error) {
      console.error(error)
      toast({
        title: 'Login Error',
        description: (error as any).message,
        variant: 'destructive'
      })
    }
    setIsLoading(false)
  }

  return (
    <div className={cn('grid gap-6', className)} {...props}>
      <form onSubmit={onSubmit}>
        <div className="grid gap-2">
          <div className="grid gap-1">
            <Label className="sr-only" htmlFor="email">
              Email
            </Label>
            <Input
              autoCapitalize="none"
              autoComplete="email"
              autoCorrect="off"
              disabled={isLoading}
              id="email"
              onChange={(event) => setEmail(event.target.value)}
              placeholder="name@example.com"
              type="email"
              value={email}
            />
            <Label className="sr-only" htmlFor="password">
              Password
            </Label>
            <Input
              autoCapitalize="none"
              autoComplete="email"
              autoCorrect="off"
              disabled={isLoading}
              id="password"
              onChange={(event) => setPassword(event.target.value)}
              placeholder="••••••••••"
              type="password"
              value={password}
            />
          </div>
          <Button loading={isLoading} type="submit">
            Login with Email
          </Button>
        </div>
      </form>
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="px-2 bg-accent text-muted-foreground">
            Or continue with
          </span>
        </div>
      </div>
      <Button
        loading={isLoading}
        onClick={() => {
          setIsLoading(true)
          Auth.federatedSignIn({
            provider: 'Epilot' as CognitoHostedUIIdentityProvider
          })
          setIsLoading(false)
        }}
        type="button"
        variant="outline"
      >
        <Icons.microsoft className="w-6 h-6 mr-2" />
        Microsoft
      </Button>
    </div>
  )
}
